import { Entity } from '@api';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { BrandAndProductDrawer } from '@terminal/modules/brand-and-product-drawer/BrandAndProductDrawer';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useBrandsAndProducts } from '@terminal/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { appPaths } from '@terminal/routes/app-paths';
import { useBrandAndProductDrawer } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useSearchParams } from 'react-router';

import { LoadingCell } from '../shared/loading-cell/LoadingCell';
import styles from './BrandsAndProducts.module.scss';

export interface BrandsAndProductsProps {
  entity: Entity;
}

export const BrandsAndProducts = ({ entity }: BrandsAndProductsProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const {
    data: brandsAndProducts = [],
    isFetching,
    error,
  } = useBrandsAndProducts(entity.tag);
  const { openDrawer } = useBrandAndProductDrawer({ entityTag: entity.tag });

  if (error) {
    throw error;
  }

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag: entity.tag,
    searchParams,
    subtab: KnowledgeGraphTabType.BrandsAndProducts,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const topItems = [...brandsAndProducts]
    .sort((a, b) => a.relationship.rank - b.relationship.rank)
    .slice(0, 3);

  if (!topItems.length && !isFetching) {
    return null;
  }

  return (
    <div>
      <SectionTitle
        title={t(
          'widget:assetOverview.knowledgeGraph.subtab.brandsAndProducts'
        )}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('brandAndProducts')}
      />
      {isFetching ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div
              className={clsx(styles.row, styles.loadingRow)}
              data-brand-row
              key={idx}
            >
              <LoadingCell />
            </div>
          ))}
        </div>
      ) : (
        <div>
          {topItems.map(item => (
            <div
              className={styles.row}
              data-brand-row
              key={item.relationship.id}
              onClick={() => openDrawer(item.product)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openDrawer(item.product);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={styles.nameWrapper}>
                <span className={styles.name}>{item.product}</span>
              </div>
              <TopRankIcon rank={item.relationship.rank} iconSize={28} />
            </div>
          ))}
        </div>
      )}
      <BrandAndProductDrawer entity={entity} />
    </div>
  );
};
